/**
 * @fileoverview Tour guiado do onboarding: realça (spotlight) o elemento-alvo da
 * tela atual marcado com data-onboarding-tour="<taskKey>", exibindo um tooltip
 * com explicação curta e o botão "Entendi". Sem dependência de bibliotecas de
 * tour — usa createPortal + recorte via box-shadow. Se o alvo não existir na
 * tela (página sem instrumentação), mostra um card central com a mesma dica.
 */

import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { makeStyles } from "@material-ui/core";

import { i18n } from "../../../translate/i18n";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";

const PAD = 8;

const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === "dark";
  return {
    overlay: { position: "fixed", inset: 0, zIndex: 2000, pointerEvents: "none" },
    hole: {
      position: "fixed",
      borderRadius: 12,
      boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
      border: "2px solid #5B7CFF",
      transition: "all 220ms cubic-bezier(0.4,0,0.2,1)",
      pointerEvents: "none"
    },
    dimFallback: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      pointerEvents: "auto"
    },
    card: {
      position: "fixed",
      zIndex: 2001,
      width: "min(320px, calc(100vw - 32px))",
      padding: 18,
      borderRadius: 16,
      pointerEvents: "auto",
      background: dark ? "linear-gradient(180deg, #171a20 0%, #111319 100%)" : "#fff",
      border: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(15,23,42,0.1)",
      boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
      fontFamily: '"Montserrat", sans-serif'
    },
    cardTitle: { fontSize: "1rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827", marginBottom: 6 },
    cardDesc: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.7)" : "#6b7280", lineHeight: 1.45 },
    cardActions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 },
    btnPrimary: {
      height: 34, padding: "0 16px", borderRadius: 9, border: "none", cursor: "pointer",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)", color: "#fff",
      fontSize: "0.83rem", fontWeight: 600, fontFamily: "inherit"
    }
  };
});

const TourSpotlight = () => {
  const classes = useStyles();
  const { activeTour, endTour, progress } = useOnboarding();
  const [rect, setRect] = useState(null);

  const tourId = activeTour?.tourId;

  const locate = useCallback(() => {
    if (!tourId) return null;
    const el = document.querySelector(`[data-onboarding-tour="${tourId}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
  }, [tourId]);

  useEffect(() => {
    if (!tourId) {
      setRect(null);
      return undefined;
    }
    let raf;
    let tries = 0;
    const tick = () => {
      const r = locate();
      if (r) {
        setRect(r);
      } else if (tries < 60) {
        tries += 1;
        raf = requestAnimationFrame(tick);
      } else {
        setRect(null); // fallback central
      }
    };
    tick();

    const onMove = () => {
      const r = locate();
      if (r) setRect(r);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [tourId, locate]);

  if (!activeTour) return null;

  const title = i18n.t(`onboarding.tasks.${activeTour.i18nKey}.label`);
  const desc = i18n.t(`onboarding.tasks.${activeTour.i18nKey}.desc`);

  // Posição do card: abaixo do alvo (ou central no fallback).
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + 12;
    const placeBelow = below + 160 < window.innerHeight;
    cardStyle = {
      top: placeBelow ? below : Math.max(12, rect.top - 172),
      left: Math.min(Math.max(12, rect.left), window.innerWidth - 332)
    };
  } else {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const node = (
    <>
      <div className={classes.overlay}>
        {rect ? (
          <div className={classes.hole} style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
        ) : (
          <div className={classes.dimFallback} onClick={endTour} />
        )}
      </div>
      <div className={classes.card} style={cardStyle}>
        <div className={classes.cardTitle}>{title}</div>
        <div className={classes.cardDesc}>{desc}</div>
        <div className={classes.cardActions}>
          <button type="button" className={classes.btnPrimary} onClick={endTour}>
            {i18n.t("onboarding.tour.gotit")}
          </button>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default TourSpotlight;
